import { createContext, useContext, type ReactNode } from 'react';
import { DEFAULT_PROPERTY, userById, type User } from '@kilroy/shared';
import type { BootState } from '../boot';

interface Session extends BootState {
  user: User;
  /** Which ground this device is working. One property at a time. */
  propertyId: string;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({
  value,
  children,
}: {
  value: BootState;
  children: ReactNode;
}) {
  return (
    <Ctx.Provider
      value={{
        ...value,
        /* The signed-in person, by preference.
         *
         * The session carries the name and the real users.id from the JWT;
         * userById is a roster fallback for a device that has not signed in
         * yet. Reaching for the roster first is what made every "mine" filter
         * compare a slug against a uuid and match nothing. */
        user: value.session
          ? {
              id: value.session.userId,
              name: value.session.name,
              title: value.session.role === 'admin' ? 'Project lead' : 'Geologist',
              initials: value.session.name
                .split(' ')
                .map((w) => w[0])
                .slice(0, 2)
                .join(''),
              role: value.session.role as 'admin' | 'geology_team_member',
              email: value.session.email,
              active: true,
            }
          : userById(value.userId),
        propertyId: DEFAULT_PROPERTY,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
